import { ProcedureRowData } from "../../tables/procedure/procedure-rows";
import { FeaturedCategory } from "../categories";
import { TableRow } from "../../utils/tables-metadata";

/**
 * CPT codes for ADL (Activities of Daily Living) procedures
 */
const ADL_CPT_CODES = [
  "97530", // Therapeutic activities, direct (one-on-one) patient contact
  "97535", // Self-care/home management training (ADL and compensatory training)
];

/**
 * CPT and SNOMED-CT codes for eye exam procedures
 */
const EYE_EXAM_CODES = [
  "92225", // Special eye exam, initial - Ophthalmoscopy extended retinal drawing
  "2022F", // Dilated retinal eye exam with evidence of retinopathy (DM)
  "722161008", // Diabetic retinal eye exam (SNOMED-CT)
  "2023F", // Dilated retinal eye exam without evidence of retinopathy (DM)
  "92226", // Special eye exam, subsequent
  "CPT10067", // Diabetic eye exam - external
  "360387", // Eye exam general
  "999999", // DEE (Diabetic Eye Exam)
];

/**
 * CPT codes for DEXA (bone density) procedures
 */
const DEXA_CPT_CODES = [
  "77080", // Dual-energy X-ray absorptiometry (DXA), bone density study
];

/**
 * CPT codes for cognitive impairment assessment procedures (including MoCA)
 */
const COGNITIVE_ASSESSMENT_CPT_CODES = [
  "99483", // Assessment and care planning for patient with cognitive impairment
];

/**
 * Filter ADL (Activities of Daily Living) procedures for Featured section
 */
export function getADLProcedures(procedures: ProcedureRowData[]): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = procedure.originalData.code?.coding ?? [];
    return codings.some(coding => coding.code && ADL_CPT_CODES.includes(coding.code));
  });
}

/**
 * Filter eye exam procedures for Featured section
 */
export function getEyeExamProcedures(procedures: ProcedureRowData[]): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = procedure.originalData.code?.coding ?? [];
    return codings.some(coding => coding.code && EYE_EXAM_CODES.includes(coding.code));
  });
}

/**
 * Filter DEXA (bone density) procedures for Featured section
 */
export function getDEXAProcedures(procedures: ProcedureRowData[]): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = procedure.originalData.code?.coding ?? [];
    return codings.some(coding => coding.code && DEXA_CPT_CODES.includes(coding.code));
  });
}

/**
 * Filter MoCA procedures for Featured section
 */
export function getMoCAProcedures(procedures: ProcedureRowData[]): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = procedure.originalData.code?.coding ?? [];
    return codings.some(
      coding => coding.code && COGNITIVE_ASSESSMENT_CPT_CODES.includes(coding.code)
    );
  });
}

export function getFeaturedQualityScreening(): FeaturedCategory {
  return {
    category: "Quality Screening",
    subCategories: [
      {
        subCategory: "ADL (Activities of Daily Living)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) => getADLProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "Eye Exam",
        tableType: "Procedure",
        getRows: (data: TableRow[]) => getEyeExamProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "DEXA (Bone Density)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) => getDEXAProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "MoCA (Cognitive Assessment)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) => getMoCAProcedures(data as ProcedureRowData[]),
      },
    ],
  };
}
